/* eslint-disable i18next/no-literal-string */

import { getCookie } from 'common/utilities/Cookie';
import { pageDataService } from 'common/utilities/PageDataService';

import { enforceApiCallRateLimit, notifyApiFailure } from './ExceptionCodePolicy';

export type DefenderChatArtifactType = 'DeviceAlert' | 'IdentityMonitoringBreach' | 'CreditAlert';

export interface DefenderChatHistoryItem {
  question?: string;
  response?: string;
}

export interface DefenderChatRequest {
  question?: string;
  history?: DefenderChatHistoryItem[];
  artifactType?: DefenderChatArtifactType;
  artifactId?: string;
  useExperimentalModel?: boolean;
  systemPrompt?: string;
  temperature?: number;
  topP?: number;
  seed?: number;
}

export interface ChatTokenUsage {
  promptTokens?: number;
  completionTokens?: number;
}

export interface DefenderChatResponseMetadata {
  duration?: string;
  searchResult?: string;
  functionCallResult?: Record<string, string>;
  chatTokenUsage?: ChatTokenUsage;
}

export interface DefenderChatResponse {
  message?: string;
  metadata?: DefenderChatResponseMetadata;
}

interface CopilotErrorResponse {
  exceptionCode?: number | string;
  message?: string;
}

type CopilotApiError = Error & {
  code?: string;
  statusCode?: number;
};

const parseErrorResponseBody = (responseText: string): CopilotErrorResponse => {
  if (!responseText) {
    return {};
  }

  try {
    return JSON.parse(responseText) as CopilotErrorResponse;
  } catch {
    return { message: responseText };
  }
};

const getRequestHeaders = (): Headers => {
  const headers = new Headers({
    Accept: 'application/json, text/json, text/plain',
    'Content-Type': 'application/json',
    IsWebView: pageDataService.isWebView().toString(),
  });

  const clientUserAgent = pageDataService.isWebView() ? getCookie('Client-User-Agent') : 'Platform/Web';
  if (clientUserAgent) {
    headers.set('Client-User-Agent', clientUserAgent);
  }

  const antiforgeryToken = getCookie('XSRF-TOKEN');
  if (antiforgeryToken) {
    headers.set('X-XSRF-TOKEN', antiforgeryToken);
  }

  return headers;
};

const getChatCompletion = async ({ body }: { body: DefenderChatRequest }): Promise<DefenderChatResponse> => {
  const host = pageDataService.getConsumerSecurityCopilotHost().replace(/\/+$/, '');
  const url = `${host}/api/v1/chat`;
  await enforceApiCallRateLimit(url);

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getRequestHeaders(),
    body: JSON.stringify(body),
  });

  const responseText = await response.text();
  if (response.status !== 200) {
    const errorResponse = parseErrorResponseBody(responseText);
    const exceptionCode = errorResponse.exceptionCode?.toString();
    if (exceptionCode) {
      notifyApiFailure(exceptionCode);
    }

    const error = new Error(errorResponse.message || response.statusText) as CopilotApiError;
    error.code = exceptionCode;
    error.statusCode = response.status;
    throw error;
  }

  return JSON.parse(responseText) as DefenderChatResponse;
};

export const microsoftConsumerSecurityCopilotClient = {
  chat: {
    getChatCompletion,
  },
};
